import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { FilmSearchResultDto } from '@filmsloth/shared';
import { api } from '../api/client.js';
import { workshopApi, ApiError } from '../api/workshop.js';
import { COMMUNITY_CATALOG } from '../lib/features.js';
import { useT } from '../i18n/index.js';
import { suggestErrorKey } from '../i18n/errorKeys.js';
import { useSession } from '../session/useSession.js';

type Step = 'search' | 'create' | 'done';

/**
 * Puts a film in front of the whole group, not just the person suggesting it.
 *
 * Since the catalog comes from TMDB, the search runs against it and a film it
 * does not have cannot be added from here: nobody could say where it streams.
 * The create step below is the community catalog's and only appears with
 * `COMMUNITY_CATALOG` on.
 *
 * Searching first is the point: a title the database already has never gets
 * a second row, either because it can go straight into the deck (its facts
 * are settled) or because it is still waiting on the community to settle
 * them, in which case adding it again would only create a duplicate. Only a
 * genuinely new title reaches the create form, and that form asks for
 * nothing but the title — year, runtime, genre and provider are all things
 * the Filmwerkstatt can fill in later; requiring them up front would just
 * stop people suggesting films they can't fully describe from memory.
 */
export function SuggestFilmModal() {
  const t = useT();
  const { credentials } = useSession();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>('search');

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<FilmSearchResultDto[]>([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);

  const [title, setTitle] = useState('');

  const [busy, setBusy] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const requestId = useRef(0);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setSearched(false);
      return;
    }
    const id = ++requestId.current;
    setSearching(true);
    const timer = window.setTimeout(() => {
      (COMMUNITY_CATALOG ? workshopApi.searchFilms(trimmed) : api.searchCatalog(trimmed))
        .then(({ results: found }) => {
          if (requestId.current !== id) return;
          setResults(found);
          setSearched(true);
        })
        .catch(() => {
          if (requestId.current !== id) return;
          setResults([]);
          setSearched(true);
        })
        .finally(() => {
          if (requestId.current === id) setSearching(false);
        });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  const reset = () => {
    setStep('search');
    setQuery('');
    setResults([]);
    setSearched(false);
    setTitle('');
    setErrorCode(null);
    setBusy(false);
  };

  const close = () => {
    if (busy) return;
    setOpen(false);
    reset();
  };

  if (!open) {
    return (
      <button
        type="button"
        className="btn btn--ghost small add-film__toggle"
        onClick={() => setOpen(true)}
      >
        + {t('film.suggestTitle')}
      </button>
    );
  }

  const suggestExisting = async (movieId: string) => {
    if (busy || !credentials) return;
    setBusy(true);
    setErrorCode(null);
    try {
      await api.suggestExistingFilm(credentials.token, movieId);
      setStep('done');
      window.setTimeout(close, 1200);
    } catch (cause) {
      setErrorCode(cause instanceof ApiError ? cause.code : 'unknown_error');
    } finally {
      setBusy(false);
    }
  };

  const startCreate = () => {
    setTitle(query.trim());
    setErrorCode(null);
    setStep('create');
  };

  const submitCreate = async (event: FormEvent) => {
    event.preventDefault();
    const trimmedTitle = title.trim();
    if (busy || !credentials || !trimmedTitle) {
      setErrorCode('invalid_request');
      return;
    }

    setBusy(true);
    setErrorCode(null);
    try {
      await api.suggestNewFilm(credentials.token, { title: trimmedTitle });
      setStep('done');
      window.setTimeout(close, 1200);
    } catch (cause) {
      setErrorCode(cause instanceof ApiError ? cause.code : 'unknown_error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="suggestion-modal__overlay" onClick={close}>
      <div className="suggestion-modal__form add-film" onClick={(event) => event.stopPropagation()}>
        <div className="suggestion-modal__header">
          <h2>{t('film.suggestTitle')}</h2>
          <button
            type="button"
            className="suggestion-modal__close"
            onClick={close}
            aria-label={t('common.close')}
            disabled={busy}
          >
            ✕
          </button>
        </div>

        <div className="suggestion-modal__content stack">
          {step === 'done' ? (
            <p className="suggestion-modal__success small">{t('film.suggestSuccess')}</p>
          ) : step === 'search' ? (
            <>
              <p className="small muted">{t('film.suggestHint')}</p>
              <div className="field">
                <label htmlFor="suggest-film-query" className="field__label">
                  {t('film.titleLabel')}
                </label>
                <input
                  id="suggest-film-query"
                  className="input"
                  type="text"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  autoFocus
                />
              </div>

              {errorCode ? (
                <p className="suggestion-modal__error small">{t(suggestErrorKey(errorCode))}</p>
              ) : null}

              {searching ? <p className="small muted">{t('film.searching')}</p> : null}

              {!searching && results.length > 0 ? (
                <ul className="suggest-film__results">
                  {results.map((result) => (
                    <li key={result.id} className="suggest-film__result">
                      <div>
                        <span>{result.title}</span>
                        {result.year ? <span className="muted small"> ({result.year})</span> : null}
                      </div>
                      {result.deckReady ? (
                        <button
                          type="button"
                          className="btn btn--primary btn--small"
                          onClick={() => void suggestExisting(result.id)}
                          disabled={busy}
                        >
                          {t('film.suggestAction')}
                        </button>
                      ) : (
                        <span className="small muted">{t('film.suggestPending')}</span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : null}

              {!searching && searched && results.length === 0 ? (
                <div className="stack stack--tight">
                  <p className="small muted">{t('film.suggestNoResults')}</p>
                  {COMMUNITY_CATALOG ? (
                    <button type="button" className="btn btn--block" onClick={startCreate}>
                      {t('film.suggestAddNew')}
                    </button>
                  ) : null}
                </div>
              ) : null}

              {COMMUNITY_CATALOG && !searching && searched && results.length > 0 ? (
                <div className="stack stack--tight">
                  <p className="small muted">{t('film.suggestNotListed')}</p>
                  <button type="button" className="btn btn--ghost btn--block" onClick={startCreate}>
                    {t('film.suggestAddNewTitled', { title: query.trim() })}
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            <form className="stack" onSubmit={submitCreate}>
              <p className="small muted">{t('film.suggestCreateHint')}</p>

              <div className="field">
                <label htmlFor="suggest-film-title" className="field__label">
                  {t('film.titleLabel')} <span className="required">*</span>
                </label>
                <input
                  id="suggest-film-title"
                  className="input"
                  type="text"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  disabled={busy}
                  required
                />
              </div>

              {errorCode === 'invalid_request' ? (
                <p className="small add-film__error">{t('film.suggestCreateRequired')}</p>
              ) : errorCode === 'duplicate_film' ? (
                <p className="suggestion-modal__error small">{t('film.duplicate')}</p>
              ) : errorCode ? (
                <p className="suggestion-modal__error small">{t(suggestErrorKey(errorCode))}</p>
              ) : null}

              <button
                type="submit"
                className="btn btn--primary btn--block"
                disabled={busy || !title.trim()}
              >
                {busy ? t('film.submitting') : t('film.suggestAction')}
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--block"
                onClick={() => setStep('search')}
                disabled={busy}
              >
                {t('film.suggestBackToSearch')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
